"use client";

import { CheckIcon, ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  type FormControlProps,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from "@chakra-ui/react";
import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getPortaledSurfaceProps } from "@/lib/theme/portaled-surface";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import {
  CUSTOM_SELECT_MENU_MIN_WIDTH,
  getCustomSelectMenuPopperProps,
  CUSTOM_SELECT_MENU_POPPER_PROPS,
} from "./customSelectMenuPopperProps";

function getMenuListSurfaceProps(
  theme: ReturnType<typeof useThemeTokens>,
  interactive: ReturnType<typeof useInteractiveTokens>,
) {
  return getPortaledSurfaceProps(theme, interactive, "menu");
}

const Wrapper = ({
  label,
  children,
  formControlProps,
  required,
}: {
  label?: string | null;
  children: React.ReactNode;
  formControlProps?: FormControlProps;
  required?: boolean;
}) => {
  const theme = useThemeTokens();

  return (
    <FormControl w="100%" {...formControlProps}>
      {label ? (
        <FormLabel
          ms="4px"
          mb="5px"
          fontSize="sm"
          fontWeight="500"
          color={theme.text.primary}
          letterSpacing="-0.01em"
        >
          {label}
          {required && (
            <Text as="span" color={theme.accent.primary} ml={1}>
              *
            </Text>
          )}
        </FormLabel>
      ) : null}

      {children}
    </FormControl>
  );
};

const SelectTrigger = ({
  children,
  variant = "outline",
  isDisabled = false,
  isInvalid = false,
  /** Taller trigger when the label has a second line (detail). */
  allowMultiline = false,
  dense = false,
  "data-testid": dataTestId,
}: {
  children: React.ReactNode;
  variant?: "primary" | "outline";
  isDisabled?: boolean;
  isInvalid?: boolean;
  allowMultiline?: boolean;
  dense?: boolean;
  "data-testid"?: string;
}) => {
  const theme = useThemeTokens();

  const variantStyles =
    variant === "primary"
      ? {
          bg: theme.accent.primary,
          color: "white",
          borderColor: isInvalid ? theme.accent.primary : theme.accent.primary,
          sx: {
            "@media (hover: hover)": {
              "&:hover:not(:disabled)": {
                bg: theme.accent.primary,
                opacity: 0.9,
              },
              // Disabled primary buttons should not change background on hover, only cursor
              "&:hover:disabled": {
                bg: `${theme.accent.primary} !important`,
                opacity: "0.5 !important",
                cursor: "not-allowed",
              },
            },
            "@media (hover: none) and (pointer: coarse)": {
              "&:active:not(:disabled)": {
                bg: theme.accent.primary,
                opacity: 0.85,
              },
            },
            // When menu is open (data-active), primary variant MUST keep its color from color palette when enabled
            "&[data-active]:not(:disabled)": {
              bg: `${theme.accent.primary} !important`,
            },
          },
          _focus: {
            bg: theme.accent.primary,
          },
          _focusVisible: {
            bg: theme.accent.primary,
            boxShadow: theme.focusRing,
          },
          _disabled: {
            opacity: 0.5,
            cursor: "not-allowed",
          },
        }
      : {
          borderWidth: "1px",
          borderColor: isInvalid
            ? theme.accent.primary
            : theme.borderGlass.default,
          bg: "transparent",
          color: theme.text.primary,
          sx: {
            "@media (hover: hover)": {
              "&:hover": {
                borderColor: isInvalid
                  ? theme.accent.primary
                  : theme.accent.primaryBorder,
                bg: "transparent",
              },
            },
            "@media (hover: none) and (pointer: coarse)": {
              "&:active": {
                borderColor: theme.accent.primary,
                bg: "transparent",
              },
            },
          },
          _focus: {
            bg: "transparent",
          },
          _focusVisible: {
            borderColor: theme.accent.primary,
            boxShadow: theme.focusRing,
            bg: "transparent",
          },
          _disabled: {
            opacity: 0.4,
            cursor: "not-allowed",
          },
        };

  return (
    <MenuButton
      w="100%"
      minH={dense ? "36px" : "44px"}
      h={allowMultiline ? "auto" : dense ? "36px" : "44px"}
      py={allowMultiline ? 2 : 0}
      as={Button}
      fontSize="sm"
      data-testid={dataTestId}
      textAlign="left"
      fontWeight="500"
      variant={variant}
      borderRadius="8px"
      px={dense ? 2 : 3}
      rightIcon={
        <ChevronDownIcon color={variant === "primary" ? "white" : undefined} />
      }
      isDisabled={isDisabled}
      transition="all 0.18s ease-out"
      data-variant={variant}
      {...variantStyles}
      sx={{
        // Force primary variant to always have background color when enabled
        ...(variant === "primary" && !isDisabled
          ? {
              bg: `${theme.accent.primary} !important`,
            }
          : {}),
        "@media (hover: hover)": {
          "&:hover:not(:focus):not(:disabled)":
            variant === "primary"
              ? {
                  bg: `${theme.accent.primary} !important`,
                  opacity: 0.9,
                }
              : {
                  borderColor: theme.accent.primaryBorder,
                  bg: "transparent !important",
                },
          // Disabled buttons should not change background on hover, only cursor
          "&:hover:disabled":
            variant === "primary"
              ? {
                  bg: `${theme.accent.primary} !important`,
                  opacity: "0.5 !important",
                  cursor: "not-allowed",
                }
              : {
                  bg: "transparent !important",
                  borderColor: `${theme.borderGlass.default} !important`,
                  cursor: "not-allowed",
                },
        },
        // Override any data-select attribute styles
        "&[data-select]": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        "&[data-select]:hover": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        "&[data-select]:focus": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        // Override any data-active attribute styles (Chakra UI active state - when menu is open)
        // Primary variant MUST keep its color from color palette when enabled
        "&[data-active]": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        "&[data-active]:hover": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        "&[data-active]:focus": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        "&:active": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        "&:focus": {
          bg:
            variant === "primary" && !isDisabled
              ? `${theme.accent.primary} !important`
              : variant === "primary"
                ? `${theme.accent.primary} !important`
                : "transparent !important",
        },
        // Ensure disabled state has appropriate background
        "&:disabled": {
          bg:
            variant === "primary"
              ? `${theme.accent.primary} !important`
              : "transparent !important",
          cursor: "not-allowed",
          opacity: variant === "primary" ? "0.5 !important" : "1 !important",
        },
        "&:disabled:hover": {
          bg:
            variant === "primary"
              ? `${theme.accent.primary} !important`
              : "transparent !important",
        },
      }}
    >
      <Box
        as="span"
        display="block"
        flex="1"
        minW={0}
        overflow="hidden"
        textAlign="left"
      >
        {children}
      </Box>
    </MenuButton>
  );
};

export type CustomSelectOption =
  | string
  | {
      label: string;
      value: string;
      /** Second line, e.g. status (smaller in menu + trigger). */
      detail?: string;
      isDisabled?: boolean;
      /** Destructive actions (e.g. delete) render in red. */
      tone?: "default" | "destructive";
    };

type NormalizedSelectOption = {
  label: string;
  value: string;
  detail?: string;
  isDisabled?: boolean;
  tone: "default" | "destructive";
};

const normalizeOption = (option: CustomSelectOption): NormalizedSelectOption =>
  typeof option === "string"
    ? { label: option, value: option, tone: "default" }
    : {
        label: option.label,
        value: String(option.value),
        detail: option.detail,
        isDisabled: option.isDisabled,
        tone: option.tone ?? "default",
      };

export interface CustomSelectProps {
  value: string;
  placeholder?: string;
  label?: string | null;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  formControlProps?: FormControlProps;
  error?: { message?: string };
  variant?: "primary" | "outline";
  required?: boolean;
  /** Helper text shown below the field when there is no error. */
  helpText?: string;
  /** Shorter trigger for dense table layouts; avoids truncating short values. */
  dense?: boolean;
  /** Keeps the options menu no wider than the trigger button. */
  menuMatchTriggerWidth?: boolean;
  "data-testid"?: string;
}

export const CustomSelect = ({
  label,
  value,
  options,
  onChange,
  formControlProps,
  placeholder = "Select...",
  error,
  variant = "outline",
  required = false,
  helpText,
  dense = false,
  menuMatchTriggerWidth = false,
  "data-testid": dataTestId,
}: CustomSelectProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const menuListSurfaceProps = getMenuListSurfaceProps(theme, interactive);
  const menuPopperProps = useMemo(
    () =>
      getCustomSelectMenuPopperProps({
        matchTriggerWidthOnly: menuMatchTriggerWidth,
      }),
    [menuMatchTriggerWidth],
  );
  const normalizedOptions = (options || []).map(normalizeOption);

  const selectedOption = normalizedOptions.find(
    (option) => String(option.value) === String(value),
  );

  const display = selectedOption ? selectedOption.label : placeholder;
  const hasDetail = Boolean(selectedOption?.detail);
  const triggerTextColor =
    variant === "primary"
      ? "white"
      : selectedOption
        ? theme.text.primary
        : theme.text.secondary;
  const triggerDetailColor =
    variant === "primary" ? "whiteAlpha.800" : theme.text.tertiary;
  const triggerBody =
    selectedOption && selectedOption.detail ? (
      <Box w="100%" minW={0} textAlign="left">
        <Text
          as="span"
          display="block"
          fontSize="sm"
          fontWeight="500"
          color={triggerTextColor}
          noOfLines={1}
          title={selectedOption.label}
        >
          {selectedOption.label}
        </Text>
        <Text
          as="span"
          display="block"
          fontSize="10px"
          lineHeight="1.2"
          fontWeight="500"
          color={triggerDetailColor}
          noOfLines={1}
          mt="2px"
        >
          {selectedOption.detail}
        </Text>
      </Box>
    ) : (
      <Text
        as="span"
        display="block"
        w="100%"
        minW={0}
        whiteSpace="nowrap"
        overflow={dense ? "visible" : "hidden"}
        textOverflow={dense ? "clip" : "ellipsis"}
        fontWeight={selectedOption ? "500" : "400"}
        color={triggerTextColor}
        title={typeof display === "string" ? display : undefined}
      >
        {display}
      </Text>
    );

  const isInvalid = formControlProps?.isInvalid || !!error;

  return (
    <Wrapper
      label={label}
      required={required}
      formControlProps={{
        ...formControlProps,
        isInvalid,
      }}
    >
      <Box w="100%" minW={0}>
      <Menu
        isLazy
        closeOnSelect
        autoSelect={false}
        {...menuPopperProps}
      >
        <SelectTrigger
          variant={variant}
          isDisabled={formControlProps?.isDisabled}
          isInvalid={isInvalid}
          allowMultiline={hasDetail}
          dense={dense}
          data-testid={dataTestId}
        >
          {triggerBody}
        </SelectTrigger>

        <Portal>
          <MenuList
            minW={
              menuMatchTriggerWidth
                ? "0"
                : `${CUSTOM_SELECT_MENU_MIN_WIDTH}px`
            }
            w="100%"
            maxW={menuMatchTriggerWidth ? "100%" : undefined}
            overflowX="hidden"
            maxH="250px"
            overflowY="auto"
            zIndex={2000}
            {...menuListSurfaceProps}
            borderWidth="1px"
            borderColor={theme.borderGlass.default}
            borderRadius="8px"
            boxShadow={theme.shadow.card}
            py={2}
            px={1.5}
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                bg: theme.scrollbar.track,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                bg: theme.scrollbar.thumb,
                borderRadius: "4px",
                "&:hover": {
                  bg: theme.scrollbar.thumbHover,
                },
              },
            }}
          >
            {normalizedOptions.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              const isDestructive = opt.tone === "destructive";

              return (
                <MenuItem
                  key={opt.value}
                  isDisabled={opt.isDisabled}
                  onClick={() => {
                    if (!opt.isDisabled) onChange(opt.value);
                  }}
                  color={
                    isDestructive
                      ? "red.400"
                      : isSelected
                        ? theme.accent.primary
                        : theme.text.primary
                  }
                  bg={
                    isSelected && !isDestructive
                      ? theme.accent.primaryBg
                      : "transparent"
                  }
                  borderRadius="8px"
                  w="100%"
                  mx={0}
                  my={0}
                  py={2}
                  px={3}
                  fontSize="sm"
                  fontWeight="500"
                  h="auto"
                  minH="auto"
                  whiteSpace="normal"
                  transition="all 0.15s ease-out"
                  sx={{
                    "@media (hover: hover)": {
                      "&:hover:not(:disabled)": isDestructive
                        ? {
                            bg: "red.50",
                            color: "red.500",
                          }
                        : {
                            bg: theme.accent.primaryBg,
                            color: theme.accent.primary,
                          },
                    },
                  }}
                  _focus={{
                    bg: isDestructive ? "red.50" : theme.accent.primaryBg,
                    color: isDestructive ? "red.500" : theme.accent.primary,
                  }}
                >
                  {opt.detail ? (
                    <Box w="100%" lineHeight="1.3">
                      <Flex align="flex-start" justify="space-between" gap={2}>
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="500" color="inherit">
                            {opt.label}
                          </Text>
                          <Text
                            fontSize="10px"
                            color={
                              isSelected
                                ? theme.accent.muted
                                : theme.text.tertiary
                            }
                            fontWeight="500"
                            mt="3px"
                            lineHeight="1.2"
                          >
                            {opt.detail}
                          </Text>
                        </Box>
                        {isSelected ? (
                          <CheckIcon
                            boxSize="14px"
                            color={theme.accent.primary}
                            flexShrink={0}
                            mt="2px"
                          />
                        ) : null}
                      </Flex>
                    </Box>
                  ) : (
                    <Flex
                      align="center"
                      justify="space-between"
                      gap={2}
                      w="100%"
                    >
                      <Text
                        as="span"
                        display="block"
                        flex={1}
                        minW={0}
                        noOfLines={1}
                        title={opt.label}
                        wordBreak="break-all"
                      >
                        {opt.label}
                      </Text>
                      {isSelected ? (
                        <CheckIcon
                          boxSize="14px"
                          color={theme.accent.primary}
                          flexShrink={0}
                        />
                      ) : null}
                    </Flex>
                  )}
                </MenuItem>
              );
            })}
          </MenuList>
        </Portal>
      </Menu>
      </Box>
      {error && (
        <Text
          color={theme.accent.primary}
          fontSize="xs"
          fontWeight="500"
          mt={1.5}
        >
          {error.message}
        </Text>
      )}
      {!error && helpText ? (
        <FormHelperText mt={1.5}>
          <Text color={theme.text.tertiary} fontSize="xs">
            {helpText}
          </Text>
        </FormHelperText>
      ) : null}
    </Wrapper>
  );
};

export interface CustomSelectWithSearchProps extends CustomSelectProps {
  /** Placeholder for the search input inside the dropdown. */
  searchPlaceholder?: string;
  /** Called when the in-menu search text changes (e.g. for server-side filtering). */
  onSearchChange?: (query: string) => void;
  /** Debounce delay for `onSearchChange` in milliseconds. */
  searchDebounceMs?: number;
  /** When true, shows a loading state instead of options. */
  isLoadingOptions?: boolean;
  /** Minimum characters before options are shown (use with `minSearchMessage`). */
  minSearchLength?: number;
  /** Shown when `searchQuery` is shorter than `minSearchLength`. */
  minSearchMessage?: string;
  /** Shown when there are no options and not loading. */
  emptyMessage?: string;
  /** Called when the dropdown opens or closes. */
  onMenuOpenChange?: (isOpen: boolean) => void;
  /** Where the menu opens. Searchable report filters stay on top so results don't flip. */
  placement?: "top-start" | "bottom-start";
  /** When false, the menu will not flip to the opposite side as its height changes. */
  flip?: boolean;
}

function getEnabledOptionIndices(options: NormalizedSelectOption[]): number[] {
  return options
    .map((option, index) => (option.isDisabled ? -1 : index))
    .filter((index) => index >= 0);
}

function getNextHighlightedIndex(
  options: NormalizedSelectOption[],
  current: number,
  direction: 1 | -1,
): number {
  const enabled = getEnabledOptionIndices(options);
  if (enabled.length === 0) return -1;
  if (current < 0) return enabled[0];

  const currentPos = enabled.indexOf(current);
  if (currentPos < 0) return enabled[0];

  const nextPos = currentPos + direction;
  if (nextPos < 0) return enabled[0];
  if (nextPos >= enabled.length) return enabled[enabled.length - 1];
  return enabled[nextPos];
}

/** Single-select dropdown with a search bar at the top to filter options by label. */
export const CustomSelectWithSearch = ({
  label,
  value,
  options,
  onChange,
  formControlProps,
  placeholder = "Select...",
  error,
  variant = "outline",
  required = false,
  searchPlaceholder = "Search...",
  onSearchChange,
  searchDebounceMs = 300,
  isLoadingOptions = false,
  minSearchLength = 0,
  minSearchMessage = "Type to search...",
  emptyMessage = "No matches",
  onMenuOpenChange,
  placement = "bottom-start",
  flip = true,
  menuMatchTriggerWidth = false,
  "data-testid": dataTestId,
}: CustomSelectWithSearchProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const menuListSurfaceProps = getMenuListSurfaceProps(theme, interactive);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Map<number, HTMLElement>>(new Map());
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  const normalizedOptions = useMemo(
    () => (options || []).map(normalizeOption),
    [options],
  );
  const menuPopperProps = useMemo(
    () =>
      getCustomSelectMenuPopperProps({
        placement,
        flip,
        matchTriggerWidthOnly: menuMatchTriggerWidth,
      }),
    [placement, flip, menuMatchTriggerWidth],
  );

  const menuItemTextProps = menuMatchTriggerWidth
    ? {
        whiteSpace: "nowrap" as const,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }
    : { whiteSpace: "normal" as const };

  const renderOptionLabel = (label: string) =>
    menuMatchTriggerWidth ? (
      <Text
        as="span"
        display="block"
        w="100%"
        minW={0}
        noOfLines={1}
        title={label}
      >
        {label}
      </Text>
    ) : (
      label
    );

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    onMenuOpenChange?.(true);
  }, [onMenuOpenChange]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
    optionRefs.current.clear();
    onMenuOpenChange?.(false);
    onSearchChangeRef.current?.("");
  }, [onMenuOpenChange]);

  useEffect(() => {
    if (!onSearchChangeRef.current) return;

    const timer = setTimeout(() => {
      onSearchChangeRef.current?.(searchQuery);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, searchDebounceMs]);

  const filteredOptions = useMemo(() => {
    if (onSearchChange) return normalizedOptions;
    if (!searchQuery.trim()) return normalizedOptions;
    const q = searchQuery.trim().toLowerCase();
    return normalizedOptions.filter((opt) => {
      const hay = `${opt.label} ${opt.detail ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [normalizedOptions, onSearchChange, searchQuery]);

  const showMinSearchMessage =
    minSearchLength > 0 && searchQuery.trim().length < minSearchLength;

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  // Keep the search field focused while results load or shrink, so Popper
  // updates do not send the caret back to the trigger.
  useEffect(() => {
    if (!isMenuOpen) return;
    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isMenuOpen, isLoadingOptions, showMinSearchMessage, filteredOptions]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    optionRefs.current
      .get(highlightedIndex)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, filteredOptions]);

  const selectOption = useCallback(
    (opt: NormalizedSelectOption) => {
      if (opt.isDisabled) return;
      onChange(opt.value);
      handleMenuClose();
    },
    [onChange, handleMenuClose],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex((prev) =>
          getNextHighlightedIndex(filteredOptions, prev, 1),
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex((prev) =>
          getNextHighlightedIndex(filteredOptions, prev, -1),
        );
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndex >= 0) {
          const opt = filteredOptions[highlightedIndex];
          if (opt) selectOption(opt);
          return;
        }
        if (filteredOptions.length === 1) {
          selectOption(filteredOptions[0]);
        }
        return;
      }

      // Prevent Menu from handling other keys while typing in search
      e.stopPropagation();
    },
    [filteredOptions, highlightedIndex, selectOption],
  );

  const selectedOption = normalizedOptions.find(
    (option) => String(option.value) === String(value),
  );
  const display = selectedOption ? selectedOption.label : placeholder;
  const hasDetail = Boolean(selectedOption?.detail);
  const triggerBody =
    selectedOption && selectedOption.detail ? (
      <Box w="100%" minW={0} textAlign="left">
        <Text
          as="span"
          display="block"
          fontSize="sm"
          fontWeight="500"
          noOfLines={1}
        >
          {selectedOption.label}
        </Text>
        <Text
          as="span"
          display="block"
          fontSize="10px"
          lineHeight="1.2"
          fontWeight="500"
          color={theme.text.tertiary}
          noOfLines={1}
          mt="2px"
        >
          {selectedOption.detail}
        </Text>
      </Box>
    ) : (
      <Text
        as="span"
        display="block"
        w="100%"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {display}
      </Text>
    );
  const isInvalid = formControlProps?.isInvalid || !!error;

  return (
    <Wrapper
      label={label}
      required={required}
      formControlProps={{
        ...formControlProps,
        isInvalid,
      }}
    >
      <Box w="100%" minW={0}>
      <Menu
        isLazy
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        closeOnSelect
        autoSelect={false}
        {...menuPopperProps}
      >
        <SelectTrigger
          variant={variant}
          isDisabled={formControlProps?.isDisabled}
          isInvalid={isInvalid}
          allowMultiline={hasDetail}
          data-testid={dataTestId}
        >
          {triggerBody}
        </SelectTrigger>

        <Portal>
          <MenuList
            minW={
              menuMatchTriggerWidth
                ? "0"
                : `${CUSTOM_SELECT_MENU_MIN_WIDTH}px`
            }
            w={menuMatchTriggerWidth ? "100%" : "100%"}
            maxW={menuMatchTriggerWidth ? "100%" : undefined}
            overflowX="hidden"
            h={flip ? undefined : "320px"}
            maxH="320px"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            zIndex={2000}
            {...menuListSurfaceProps}
            borderWidth="1px"
            borderColor={theme.borderGlass.default}
            borderRadius="8px"
            boxShadow={theme.shadow.card}
            py={0}
          >
            <Box
              px={3}
              py={2}
              flexShrink={0}
              onClick={(e) => e.stopPropagation()}
              maxW="100%"
            >
              <InputGroup h="44px" w="100%" maxW="100%">
                <InputLeftElement h="100%">
                  <IconButton
                    size="sm"
                    bg="transparent"
                    aria-label="Search options"
                    borderRadius="inherit"
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": { bg: "transparent" },
                      },
                      "@media (hover: none) and (pointer: coarse)": {
                        "&:active": { bg: "transparent" },
                      },
                    }}
                    _focus={{ boxShadow: "none" }}
                    icon={
                      <SearchIcon
                        color={theme.text.secondary}
                        w="16px"
                        h="16px"
                      />
                    }
                  />
                </InputLeftElement>
                <Input
                  ref={searchInputRef}
                  h="44px"
                  pl="42px"
                  fontSize="sm"
                  fontWeight="500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={searchPlaceholder}
                  borderRadius="8px"
                  color={theme.text.primary}
                  _placeholder={{
                    color: theme.text.secondary,
                    fontSize: "14px",
                  }}
                  bg="transparent"
                  borderColor={theme.borderGlass.default}
                  _focus={{
                    borderColor: theme.accent.primaryBorder,
                    boxShadow: `0 0 0 1px ${theme.accent.primaryBorder}`,
                    bg: "transparent !important",
                  }}
                  _hover={{ bg: "transparent !important" }}
                  _active={{ bg: "transparent !important" }}
                  autoFocus
                  aria-label="Search options"
                  sx={{
                    "&[data-active]": { bg: "transparent !important" },
                  }}
                />
              </InputGroup>
            </Box>
            <Box
              overflowY="auto"
              overflowX="hidden"
              flex={1}
              minH={0}
              minW={menuMatchTriggerWidth ? 0 : undefined}
              maxW="100%"
              py={2}
              px={1.5}
              display="flex"
              flexDirection="column"
              gap={1}
              sx={{
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": {
                  bg: theme.scrollbar.track,
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  bg: theme.scrollbar.thumb,
                  borderRadius: "4px",
                  "&:hover": { bg: theme.scrollbar.thumbHover },
                },
              }}
            >
              {isLoadingOptions ? (
                <Text px={3} py={2} fontSize="sm" color={theme.text.tertiary}>
                  Loading...
                </Text>
              ) : showMinSearchMessage ? (
                <>
                  {filteredOptions
                    .filter(
                      (opt) =>
                        opt.value === "" || String(opt.value) === String(value),
                    )
                    .map((opt) => (
                      <MenuItem
                        key={opt.value || "all"}
                        onClick={() => selectOption(opt)}
                        tabIndex={-1}
                        color={theme.text.primary}
                        bg="transparent"
                        borderRadius="8px"
                        w="100%"
                        mx={0}
                        my={0}
                        py={2}
                        px={3}
                        fontSize="sm"
                        fontWeight="500"
                        h="auto"
                        minH="auto"
                        transition="all 0.15s ease-out"
                        {...menuItemTextProps}
                        sx={{
                          "@media (hover: hover)": {
                            "&:hover:not(:disabled)": {
                              bg: theme.accent.primaryBg,
                              color: theme.accent.primary,
                            },
                          },
                        }}
                        _focus={{
                          bg: theme.accent.primaryBg,
                          color: theme.accent.primary,
                        }}
                      >
                        {renderOptionLabel(opt.label)}
                      </MenuItem>
                    ))}
                  <Text px={3} py={2} fontSize="sm" color={theme.text.tertiary}>
                    {minSearchMessage}
                  </Text>
                </>
              ) : filteredOptions.length === 0 ? (
                <Text px={3} py={2} fontSize="sm" color={theme.text.tertiary}>
                  {emptyMessage}
                </Text>
              ) : (
                filteredOptions.map((opt, index) => {
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <MenuItem
                      key={opt.value}
                      ref={(el) => {
                        if (el) optionRefs.current.set(index, el);
                        else optionRefs.current.delete(index);
                      }}
                      isDisabled={opt.isDisabled}
                      onClick={() => {
                        selectOption(opt);
                      }}
                      tabIndex={-1}
                      color={
                        isHighlighted
                          ? theme.accent.primary
                          : theme.text.primary
                      }
                      bg={
                        isHighlighted ? theme.accent.primaryBg : "transparent"
                      }
                      borderRadius="8px"
                      w="100%"
                      mx={0}
                      my={0}
                      py={2}
                      px={3}
                      fontSize="sm"
                      fontWeight="500"
                      h="auto"
                      minH="auto"
                      transition="all 0.15s ease-out"
                      {...menuItemTextProps}
                      sx={{
                        "@media (hover: hover)": {
                          "&:hover:not(:disabled)": {
                            bg: theme.accent.primaryBg,
                            color: theme.accent.primary,
                          },
                        },
                      }}
                      _focus={{
                        bg: theme.accent.primaryBg,
                        color: theme.accent.primary,
                      }}
                    >
                      {opt.detail ? (
                        <Box w="100%" lineHeight="1.3">
                          <Text fontSize="sm" fontWeight="500" color="inherit">
                            {opt.label}
                          </Text>
                          <Text
                            fontSize="10px"
                            color={theme.text.tertiary}
                            fontWeight="500"
                            mt="3px"
                            lineHeight="1.2"
                          >
                            {opt.detail}
                          </Text>
                        </Box>
                      ) : (
                        renderOptionLabel(opt.label)
                      )}
                    </MenuItem>
                  );
                })
              )}
            </Box>
          </MenuList>
        </Portal>
      </Menu>
      </Box>
      {error && (
        <Text
          color={theme.accent.primary}
          fontSize="xs"
          fontWeight="500"
          mt={1.5}
        >
          {error.message}
        </Text>
      )}
    </Wrapper>
  );
};

export type CustomSelectMultipleOption =
  | string
  | { value: string; label: string };

const normalizeMultipleOption = (
  opt: CustomSelectMultipleOption,
): { value: string; label: string } => {
  if (typeof opt === "string") return { value: opt, label: opt };
  const value = String(opt.value);
  return { value, label: opt.label ?? value };
};

export interface CustomSelectMultipleProps {
  label?: string;
  options: CustomSelectMultipleOption[];
  selected: string[];
  lockedOptions?: string[];
  valueLabel?: string;
  placeholder?: string;
  dropdownHeight?: string;
  formControlProps?: FormControlProps;
  onChange: (values: string[]) => void;
  required?: boolean;
  error?: { message?: string };
  testId?: string;
}

const MultipleCheckboxRow = memo(function MultipleCheckboxRow({
  label: optionLabel,
  isLocked,
  isSelected,
  onToggle,
  testOptionId,
}: {
  label: string;
  isLocked: boolean;
  isSelected: boolean;
  onToggle: () => void;
  testOptionId?: string;
}) {
  const theme = useThemeTokens();

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
    },
    [],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (isLocked) return;
      onToggle();
    },
    [isLocked, onToggle],
  );

  return (
    <Box
      as="button"
      type="button"
      role="menuitemcheckbox"
      aria-checked={isSelected}
      aria-disabled={isLocked || undefined}
      data-testid={testOptionId}
      title={
        isLocked
          ? "This location is required: the teacher is assigned to classes here. Assign those classes to another teacher first, then you can remove this location."
          : undefined
      }
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      display="flex"
      alignItems="center"
      gap={2}
      textAlign="left"
      color={theme.text.primary}
      bg="transparent"
      borderRadius="6px"
      w="calc(100% - 10px)"
      mx="5px"
      py={2}
      px={3}
      fontSize="sm"
      fontWeight="500"
      transition="background-color 0.15s ease-out, color 0.15s ease-out"
      cursor={isLocked ? "not-allowed" : "pointer"}
      opacity={isLocked ? 0.85 : 1}
      sx={{
        "@media (hover: hover)": {
          "&:hover": {
            bg: isLocked ? "transparent" : theme.accent.primaryBg,
            color: isLocked ? theme.text.primary : theme.accent.primary,
          },
        },
        "&:focus": {
          outline: "none",
        },
        "&:focus-visible": {
          outline: "none",
          bg: isLocked ? "transparent" : theme.accent.primaryBg,
          color: isLocked ? theme.text.primary : theme.accent.primary,
        },
      }}
    >
      <Box
        as="span"
        aria-hidden="true"
        flexShrink={0}
        w="16px"
        h="16px"
        borderRadius="4px"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={
          isSelected ? theme.accent.primary : theme.borderGlass.default
        }
        bg={isSelected ? theme.accent.primary : "transparent"}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        transition="all 0.15s ease-out"
      >
        {isSelected ? <CheckIcon color="white" w="10px" h="10px" /> : null}
      </Box>
      <Text as="span" fontSize="sm" fontWeight="500" color="inherit">
        {optionLabel}
      </Text>
    </Box>
  );
});

export const CustomSelectMultiple = memo(function CustomSelectMultiple({
  label,
  options,
  selected,
  lockedOptions,
  onChange,
  valueLabel,
  formControlProps,
  placeholder = "Select…",
  dropdownHeight = "250px",
  required = false,
  error,
  testId,
}: CustomSelectMultipleProps) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const menuListSurfaceProps = getMenuListSurfaceProps(theme, interactive);
  const normalizedOptions = useMemo(
    () => options.map(normalizeMultipleOption),
    [options],
  );

  const labelByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of normalizedOptions) {
      m.set(o.value, o.label);
    }
    return m;
  }, [normalizedOptions]);

  const selectedRef = useRef<string[]>(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const resolvedSelectedLabels = useMemo(
    () => selected.map((v) => labelByValue.get(v) ?? v),
    [selected, labelByValue],
  );

  const display =
    valueLabel ||
    (selected.length === 0
      ? placeholder
      : resolvedSelectedLabels.length <= 2
        ? resolvedSelectedLabels.join(", ")
        : `${selected.length} selected`);

  const isInvalid = formControlProps?.isInvalid || !!error;

  const toggleValue = useCallback(
    (value: string) => {
      const current = selectedRef.current;
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      selectedRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  return (
    <Wrapper
      label={label}
      formControlProps={{
        ...formControlProps,
        isInvalid,
      }}
      required={required}
    >
      <Menu
        closeOnSelect={false}
        autoSelect={false}
        isLazy
        {...CUSTOM_SELECT_MENU_POPPER_PROPS}
        gutter={14}
      >
        <SelectTrigger
          isDisabled={formControlProps?.isDisabled}
          isInvalid={isInvalid}
          data-testid={testId}
        >
          {display}
        </SelectTrigger>

        <Portal>
          <MenuList
            minW={`${CUSTOM_SELECT_MENU_MIN_WIDTH}px`}
            maxH={dropdownHeight}
            overflowY="auto"
            zIndex={2000}
            {...menuListSurfaceProps}
            borderWidth="1px"
            borderColor={theme.borderGlass.default}
            borderRadius="8px"
            boxShadow={theme.shadow.card}
            py={2}
            px={1.5}
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                bg: theme.scrollbar.track,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                bg: theme.scrollbar.thumb,
                borderRadius: "4px",
                "&:hover": {
                  bg: theme.scrollbar.thumbHover,
                },
              },
            }}
          >
            {normalizedOptions.map((opt) => {
              const isSelected = selected.includes(opt.value);
              const isLocked = Boolean(
                lockedOptions?.length &&
                  lockedOptions.includes(opt.value) &&
                  isSelected,
              );

              return (
                <MultipleCheckboxRow
                  key={opt.value}
                  label={opt.label}
                  isSelected={isSelected}
                  isLocked={isLocked}
                  testOptionId={testId ? `${testId}-option` : undefined}
                  onToggle={() => {
                    if (isLocked) return;
                    toggleValue(opt.value);
                  }}
                />
              );
            })}
          </MenuList>
        </Portal>
      </Menu>
      {error && (
        <Text
          color={theme.accent.primary}
          fontSize="xs"
          fontWeight="500"
          mt={1.5}
        >
          {error.message}
        </Text>
      )}
    </Wrapper>
  );
});
